import { Dispatch, ReactNode } from 'react';
import { ActionMap } from '../../API/GoMoto.types';

/**
 * Types of dispatches possible for the form context.
 */
export enum FormDispatchTypes {
    /**
     * Sets the fullWidthKioskInput prop to either `true` or `false`.
     */
    SetFullWidthKioskInputs = 'SET_FULL_WIDTH_KIOSK_INPUTS'
}

/**
 * Maps the {@link FormDispatchTypes} to the payload required for the
 * dispatch.
 */
export type FormPayload = {
    [FormDispatchTypes.SetFullWidthKioskInputs]: boolean;
};

/**
 * Map type definition to ensure the {@link FormDispatchTypes} are correctly mapped
 * to the payload types.
 */
export type FormActions = ActionMap<FormPayload>[keyof ActionMap<FormPayload>];

/**
 * The type definition for the form context state object.
 */
export interface FormContextState {
    /**
     * Determines whether all form wrappers inside of the context are styled
     * to full width or not.
     */
    fullWidthKioskInputs: boolean;
}

/**
 * The type definition for the `FormProvider` props that it takes.
 */
export interface FormProviderProps {
    /**
     * Determines whether the state `fullWidthKioskInputs` sets the styling to
     * full width or not when in the `Electron` environment.
     */
    fullWidthKioskInputs?: boolean;
    /**
     * The form children to be rendered.
     */
    children?: ReactNode;
}

/**
 * The type defintion for the form state context.
 *
 * @note It can be undefined so that the hooks throw an error
 * when used outside of the context.
 */
export type FormState = FormContextState | undefined;

/**
 * The type defintion for the form dispatch context.
 *
 * @note It can be undefined so that the hooks throw an error
 * when used outside of the context.
 */
export type FormDispatch = Dispatch<FormActions> | undefined;

/**
 * The type defintion for the form context reducer function.
 *
 * @param state - {@link FormContextState} description.
 * @param action - {@link FormActions} description.
 */
export type FormReducer = (state: FormContextState, action: FormActions) => FormContextState;

/**
 * The type definition for the `useFormState` hook return object.
 */
export type UseFormStateReturn = FormContextState;

/**
 * The type defintion for the `useFormState` function.
 */
export type UseFormStateFunction = () => UseFormStateReturn;

/**
 * The type definition for the `useFormDispatch` hook return object.
 */
export type UseFormDispatchReturn = Dispatch<FormActions>;

/**
 * The type definition for the `useFormDispatch` function.
 */
export type UseFormDispatchFunction = () => UseFormDispatchReturn;

/**
 * The type defintion for the `FormProvider` component.
 */
export type FormProviderComponent = (props: FormProviderProps) => ReactNode;